import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  CheckCircle2,
  Settings as SettingsIcon,
  RotateCcw,
  Send,
  Save,
  User,
  Calendar,
  CreditCard,
  Truck,
  Package,
  Loader2
} from 'lucide-react';
import './FormPage.css';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

const DEFAULT_NCM_LIST = [
  { codigo: '94033000', descricao: 'Móveis de madeira para escritórios' },
  { codigo: '95059000', descricao: 'Artigos para festas, carnaval ou outros divertimentos' },
  { codigo: '39269090', descricao: 'Outras obras de plástico (Adereços)' },
  { codigo: '65050090', descricao: 'Chapéus e outros artefatos de uso semelhante' },
  { codigo: '67029000', descricao: 'Flores, folhagens e frutos artificiais' }
];

const DEFAULT_CFOP_LIST = [
  { codigo: '5101', descricao: '5101 - Venda de produção do estabelecimento (Estadual)' },
  { codigo: '5102', descricao: '5102 - Venda de mercadoria adquirida de terceiros (Estadual)' },
  { codigo: '5405', descricao: '5405 - Venda de mercadoria c/ ST (Estadual)' },
  { codigo: '5949', descricao: '5949 - Outra saída de mercadoria (Estadual)' },
  { codigo: '6101', descricao: '6101 - Venda de produção fora do estado (Interestadual)' },
  { codigo: '6102', descricao: '6102 - Venda de mercadoria adquirida de terceiros (Interestadual)' },
  { codigo: '6405', descricao: '6405 - Venda de mercadoria c/ ST fora do estado (Interestadual)' },
  { codigo: '6949', descricao: '6949 - Outra saída fora do estado (Interestadual)' }
];

const DEFAULT_SIT_TRIB_LIST = [
  { codigo: '101', descricao: '101 - Tributada pelo Simples Nacional com permissão de crédito' },
  { codigo: '102', descricao: '102 - Tributada pelo Simples Nacional sem permissão de crédito' },
  { codigo: '103', descricao: '103 - Isenção do ICMS no Simples Nacional para faixa de receita bruta' },
  { codigo: '201', descricao: '201 - Tributada pelo Simples com permissão de crédito e cobrança ST' },
  { codigo: '202', descricao: '202 - Tributada pelo Simples sem permissão de crédito e cobrança ST' },
  { codigo: '300', descricao: '300 - Imune (Simples Nacional)' },
  { codigo: '400', descricao: '400 - Não tributada pelo Simples Nacional' },
  { codigo: '500', descricao: '500 - ICMS cobrado anteriormente por substituição tributária' },
  { codigo: '900', descricao: '900 - Outros (Simples Nacional)' },
  { codigo: '00', descricao: 'CST 00 - Tributada integralmente (Regime Normal)' },
  { codigo: '20', descricao: 'CST 20 - Com redução de base de cálculo (Regime Normal)' },
  { codigo: '40', descricao: 'CST 40 - Isenta (Regime Normal)' },
  { codigo: '41', descricao: 'CST 41 - Não tributada (Regime Normal)' },
  { codigo: '60', descricao: 'CST 60 - ICMS cobrado anteriormente por ST (Regime Normal)' }
];

export default function InvoiceIssue() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(null);
  const [fiscalItems, setFiscalItems] = useState([]);
  const isEmittingRef = useRef(false);

  const [ncmOptions, setNcmOptions] = useState(DEFAULT_NCM_LIST);
  const [cfopOptions, setCfopOptions] = useState(DEFAULT_CFOP_LIST);
  const [sitTribOptions, setSitTribOptions] = useState(DEFAULT_SIT_TRIB_LIST);

  const [defaults, setDefaults] = useState({
    padrao_ncm: '94033000',
    padrao_cfop: '5102',
    padrao_situacao_tributaria: '102'
  });
  const [ambienteNfe, setAmbienteNfe] = useState(2);

  useEffect(() => {
    loadOrderAndConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOrderAndConfig = async () => {
    setLoading(true);
    try {
      const [configRes, orderRes] = await Promise.all([
        fetch(`${API_BASE_URL}/configuracoes.php`),
        fetch(`${API_BASE_URL}/vendas.php?id=${id}`)
      ]);

      const configData = await configRes.json();
      const orderData = await orderRes.json();

      const loadedNcmList = Array.isArray(configData?.lista_ncm) && configData.lista_ncm.length > 0
        ? configData.lista_ncm
        : DEFAULT_NCM_LIST;
      const loadedCfopList = Array.isArray(configData?.lista_cfop) && configData.lista_cfop.length > 0
        ? configData.lista_cfop
        : DEFAULT_CFOP_LIST;
      const loadedSitList = Array.isArray(configData?.lista_situacao_tributaria) && configData.lista_situacao_tributaria.length > 0
        ? configData.lista_situacao_tributaria
        : DEFAULT_SIT_TRIB_LIST;

      const padraoNcm = configData?.padrao_ncm || (loadedNcmList[0]?.codigo ?? '94033000');
      const padraoCfop = configData?.padrao_cfop || (loadedCfopList[0]?.codigo ?? '5102');
      const padraoSit = configData?.padrao_situacao_tributaria || (loadedSitList[0]?.codigo ?? '102');

      setNcmOptions(loadedNcmList);
      setCfopOptions(loadedCfopList);
      setSitTribOptions(loadedSitList);
      setAmbienteNfe(Number(configData?.ambiente_nfe) === 1 ? 1 : 2);
      setDefaults({
        padrao_ncm: padraoNcm,
        padrao_cfop: padraoCfop,
        padrao_situacao_tributaria: padraoSit
      });

      const foundOrder = Array.isArray(orderData) && orderData.length > 0 ? orderData[0] : null;
      if (!foundOrder) {
        showAlert('Aviso', `Pedido #${id} não encontrado.`);
        navigate('/pedidos');
        return;
      }

      setOrder(foundOrder);

      const mappedItems = (foundOrder.itens || []).map((item) => ({
        venda_item_id: item.id,
        produto_id: item.produto_id,
        produto_nome: item.produto_nome,
        quantidade: Number(item.quantidade || 1),
        preco_unitario: Number(item.preco_unitario || 0),
        subtotal: Number(item.subtotal || (Number(item.quantidade || 1) * Number(item.preco_unitario || 0))),
        ncm: item.ncm_nfe || padraoNcm,
        cfop: item.cfop_nfe || padraoCfop,
        situacao_tributaria: item.situacao_tributaria_nfe || padraoSit
      }));

      setFiscalItems(mappedItems);
    } catch (error) {
      console.error('Erro ao carregar dados para emissão de Nota Fiscal:', error);
      showAlert('Erro', 'Não foi possível carregar os dados do pedido.');
    } finally {
      setLoading(false);
    }
  };

  const handleItemFiscalChange = (index, field, value) => {
    setFiscalItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  const handleResetToDefaults = () => {
    setFiscalItems(prev =>
      prev.map(item => ({
        ...item,
        ncm: defaults.padrao_ncm,
        cfop: defaults.padrao_cfop,
        situacao_tributaria: defaults.padrao_situacao_tributaria
      }))
    );
    showAlert('Padrões Aplicados', 'Todos os itens foram preenchidos com a seleção padrão das Configurações.');
  };

  const handleSaveFiscalItems = async () => {
    if (!order) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/emitir_nfe.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'salvar',
          venda_id: order.id,
          itens_fiscais: fiscalItems
        })
      });

      const result = await response.json();

      if (result.success) {
        showAlert('Sucesso', result.message || 'Dados fiscais dos itens salvos com sucesso!');
      } else {
        showAlert('Erro ao Salvar', result.error || 'Não foi possível salvar os dados fiscais dos itens.');
      }
    } catch (error) {
      console.error('Erro ao salvar itens fiscais:', error);
      showAlert('Erro', 'Ocorreu uma falha de conexão ao salvar os dados fiscais.');
    } finally {
      setSaving(false);
    }
  };

  const handleEmitInvoice = async () => {
    if (!order || issuing || isEmittingRef.current) return;
    isEmittingRef.current = true;

    try {
      const confirmMsg = ambienteNfe === 1
        ? `Deseja transmitir a Nota Fiscal Eletrônica para o pedido #${order.venda_id || order.id} com as classificações fiscais selecionadas?`
        : `Deseja transmitir a Nota Fiscal Eletrônica para o pedido #${order.venda_id || order.id}?\n\nAmbiente SEFAZ ativo: HOMOLOGAÇÃO (Ambiente de Testes - Sem Valor Fiscal)`;

      const confirmed = await showConfirm(
        'Confirmar Emissão de NF-e',
        confirmMsg
      );
      if (!confirmed) {
        isEmittingRef.current = false;
        return;
      }

      setIssuing(true);
      const response = await fetch(`${API_BASE_URL}/emitir_nfe.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venda_id: order.id,
          itens_fiscais: fiscalItems
        })
      });

      const result = await response.json();

      if (result.success) {
        await showAlert(
          'NF-e 55 Gerada com Sucesso!',
          `Número da Nota: NF-e #${result.numero_nfe}\nChave de Acesso: ${result.chave_acesso}\nStatus: ${String(result.status || '').toUpperCase()}\n\n${result.motivo_sefaz}`
        );

        if (result.chave_acesso) {
          window.open(`${API_BASE_URL}/danfe_pdf.php?chave=${result.chave_acesso}`, '_blank');
        }
        loadOrderAndConfig();
      } else {
        showAlert('Erro ao Emitir NF-e', result.error || 'Falha ao processar a emissão da nota fiscal.');
      }
    } catch (error) {
      console.error('Erro na emissão de NF-e:', error);
      showAlert('Erro', 'Ocorreu uma falha de conexão ao comunicar com o serviço de NF-e.');
    } finally {
      setIssuing(false);
      isEmittingRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="page-container animate-fade-in">
        <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          Carregando dados do pedido e configurações fiscais...
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="page-container animate-fade-in">
      {/* Cabeçalho da Página */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            type="button"
            className="btn-outline"
            onClick={() => navigate('/pedidos')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem' }}
          >
            <ArrowLeft size={16} /> Voltar
          </button>
          <div>
            <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <FileText size={24} color="var(--primary-color)" />
              <span>Nota Fiscal — Pedido #{order.venda_id || order.id}</span>
              {ambienteNfe !== 1 && (
                <span
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    padding: '0.25rem 0.65rem',
                    borderRadius: '999px',
                    backgroundColor: '#fef3c7',
                    color: '#b45309',
                    border: '1px solid #fde68a'
                  }}
                >
                  AMBIENTE: HOMOLOGAÇÃO (SEM VALOR FISCAL)
                </span>
              )}
            </h1>
            <p className="page-subtitle">
              Revise e selecione o NCM, CFOP e Situação Tributária de cada produto antes de emitir a NF-e
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn-outline"
            onClick={handleResetToDefaults}
            title="Restaurar todos os itens para o padrão das configurações"
            style={{ height: '38px', padding: '0 1rem', fontSize: '0.85rem', fontWeight: '500', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
          >
            <RotateCcw size={15} /> Aplicar Padrão das Configurações
          </button>
          <button
            type="button"
            className="btn-outline"
            onClick={() => navigate('/configuracoes')}
            title="Gerenciar listas e padrões fiscais nas Configurações"
            style={{ height: '38px', padding: '0 1rem', fontSize: '0.85rem', fontWeight: '500', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
          >
            <SettingsIcon size={15} /> Configurar Padrões
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={issuing || fiscalItems.length === 0}
            onClick={handleEmitInvoice}
            style={{
              width: 'auto',
              height: '38px',
              padding: '0 1.25rem',
              fontSize: '0.85rem',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.45rem',
              cursor: issuing ? 'not-allowed' : 'pointer',
              opacity: issuing ? 0.75 : 1,
              pointerEvents: issuing ? 'none' : 'auto'
            }}
          >
            {issuing ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Transmitindo NF-e à SEFAZ...</span>
              </>
            ) : (
              <>
                <Send size={15} />
                <span>{order.nfe ? 'Reemitir Nota Fiscal' : 'Emitir Nota Fiscal'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Resumo do Pedido */}
      <div className="card" style={{ padding: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '8px', backgroundColor: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <User size={18} />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Cliente</div>
              <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.92rem' }}>{order.cliente_nome || 'Não informado'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '8px', backgroundColor: '#f0fdf4', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CreditCard size={18} />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Valor Total do Pedido</div>
              <div style={{ fontWeight: '700', color: 'var(--text-main)', fontSize: '0.95rem' }}>
                R$ {Number(order.valor_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                {Number(order.desconto || 0) > 0 && (
                  <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#7c3aed', fontWeight: '600' }}>
                    (-{Number(order.desconto)}%)
                  </span>
                )}
                {Number(order.valor_frete || 0) > 0 && (
                  <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem', color: '#2563eb', fontWeight: '600' }}>
                    (incl. Frete: R$ {Number(order.valor_frete).toLocaleString('pt-BR', { minimumFractionDigits: 2 })})
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '8px', backgroundColor: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Calendar size={18} />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Data de Contabilização</div>
              <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.9rem' }}>{order.data_contabilizacao || '-'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '8px', backgroundColor: '#f3e8ff', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Truck size={18} />
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Transportadora</div>
              <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.9rem' }}>
                {order.transportadora_nome || 'Sem Frete / Próprio'}
                {Number(order.valor_frete || 0) > 0 && (
                  <span style={{ color: '#2563eb', fontWeight: '600', marginLeft: '0.35rem' }}>
                    — Frete: R$ {Number(order.valor_frete).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Caso já possua NF-e emitida */}
        {order.nfe && (
          <div style={{
            marginTop: '1.1rem',
            paddingTop: '1rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.85rem' }}>
              <CheckCircle2 size={18} color="#0284c7" />
              <span>
                <strong>NF-e Emitida:</strong> Nº {order.nfe.numero_nfe} | Chave: <code style={{ fontSize: '0.8rem' }}>{order.nfe.chave_acesso}</code>
              </span>
              <span className="badge badge-success" style={{ textTransform: 'uppercase' }}>
                {order.nfe.status || 'GERADA'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn-outline"
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem', borderColor: '#38bdf8', backgroundColor: '#e0f2fe', color: '#0284c7', fontWeight: '600' }}
                onClick={() => window.open(`${API_BASE_URL}/danfe_pdf.php?chave=${order.nfe.chave_acesso}`, '_blank')}
              >
                <FileText size={14} style={{ marginRight: '0.3rem' }} /> Visualizar DANFE (PDF)
              </button>
              {order.nfe.xml_path && (
                <button
                  type="button"
                  className="btn-outline"
                  style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                  onClick={() => window.open(order.nfe.xml_path.startsWith('http') ? order.nfe.xml_path : `${API_BASE_URL.replace('/src/php', '')}/${order.nfe.xml_path}`, '_blank')}
                >
                  <FileText size={14} style={{ marginRight: '0.3rem' }} /> Baixar XML
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Lista de Produtos com Dropdowns de NCM, CFOP e Situação Tributária */}
      <div className="card list-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Package size={18} color="var(--primary-color)" />
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-main)' }}>
              Itens do Pedido ({fiscalItems.length})
            </h3>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Seleção Padrão Atual: <strong>NCM {defaults.padrao_ncm}</strong> • <strong>CFOP {defaults.padrao_cfop}</strong> • <strong>Sit. Trib. {defaults.padrao_situacao_tributaria}</strong>
          </div>
        </div>

        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '28%' }}>Nome do Produto</th>
                <th style={{ width: '24%' }}>NCM</th>
                <th style={{ width: '24%' }}>CFOP</th>
                <th style={{ width: '24%' }}>Situação Tributária</th>
              </tr>
            </thead>
            <tbody>
              {fiscalItems.map((item, idx) => {
                const hasNcmInList = ncmOptions.some(o => String(o.codigo) === String(item.ncm));
                const hasCfopInList = cfopOptions.some(o => String(o.codigo) === String(item.cfop));
                const hasSitInList = sitTribOptions.some(o => String(o.codigo) === String(item.situacao_tributaria));

                return (
                  <tr key={item.venda_item_id || idx}>
                    <td>
                      <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.9rem' }}>
                        {item.produto_nome}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                        Qtd: <strong>{item.quantidade}</strong> × R$ {Number(item.preco_unitario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ={' '}
                        <strong style={{ color: 'var(--text-main)' }}>
                          R$ {Number(item.subtotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </strong>
                      </div>
                    </td>

                    {/* Dropdown NCM */}
                    <td>
                      <select
                        className="standard-input"
                        value={item.ncm}
                        onChange={e => handleItemFiscalChange(idx, 'ncm', e.target.value)}
                        style={{ width: '100%', fontSize: '0.85rem' }}
                      >
                        {!hasNcmInList && item.ncm && (
                          <option value={item.ncm}>{item.ncm}</option>
                        )}
                        {ncmOptions.map(opt => (
                          <option key={opt.codigo} value={opt.codigo}>
                            {opt.codigo}{opt.descricao ? ` - ${opt.descricao}` : ''}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Dropdown CFOP */}
                    <td>
                      <select
                        className="standard-input"
                        value={item.cfop}
                        onChange={e => handleItemFiscalChange(idx, 'cfop', e.target.value)}
                        style={{ width: '100%', fontSize: '0.85rem' }}
                      >
                        {!hasCfopInList && item.cfop && (
                          <option value={item.cfop}>{item.cfop}</option>
                        )}
                        {cfopOptions.map(opt => (
                          <option key={opt.codigo} value={opt.codigo}>
                            {opt.descricao && opt.descricao.startsWith(opt.codigo)
                              ? opt.descricao
                              : `${opt.codigo}${opt.descricao ? ` - ${opt.descricao}` : ''}`}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Dropdown Situação Tributária */}
                    <td>
                      <select
                        className="standard-input"
                        value={item.situacao_tributaria}
                        onChange={e => handleItemFiscalChange(idx, 'situacao_tributaria', e.target.value)}
                        style={{ width: '100%', fontSize: '0.85rem' }}
                      >
                        {!hasSitInList && item.situacao_tributaria && (
                          <option value={item.situacao_tributaria}>{item.situacao_tributaria}</option>
                        )}
                        {sitTribOptions.map(opt => (
                          <option key={opt.codigo} value={opt.codigo}>
                            {opt.descricao && opt.descricao.startsWith(opt.codigo)
                              ? opt.descricao
                              : `${opt.codigo}${opt.descricao ? ` - ${opt.descricao}` : ''}`}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}

              {fiscalItems.length === 0 && (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
                    Este pedido não possui itens cadastrados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Rodapé com botão de salvar */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1rem 1.5rem',
          borderTop: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-color)'
        }}>
          <button
            type="button"
            className="btn-outline"
            onClick={() => navigate('/pedidos')}
            style={{
              height: '38px',
              minWidth: '120px',
              padding: '0 1.25rem',
              fontSize: '0.85rem',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box'
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn-success"
            disabled={saving || issuing || fiscalItems.length === 0}
            onClick={handleSaveFiscalItems}
            style={{
              height: '38px',
              minWidth: '120px',
              padding: '0 1.25rem',
              fontSize: '0.85rem',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.45rem',
              boxSizing: 'border-box'
            }}
          >
            <Save size={15} />
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

